<template>
  <div
    class="v-application"
    data-qa="doctor-cases"
  >
    <PreviewCasesSlider
      :doctor-cases="doctorCases"
      :doctor-case-count="doctorCaseCount"
      @click-slide="handleClickSlide"
    />
    <CoreDrawer
      v-model="casesData.isVisibleModal"
      is-back
      :title="$t('Фото до и после', { ns: $i18next.language })"
      :width="650"
      :data-qa="isMobile ? 'doctor-cases_dialog' : 'doctor-cases_drawer'"
      @back="casesData.closeModal"
    >
      <CasesDetails
        ref="casesDetailsRef"
        :doctor="doctor"
        :cases-deatils="casesData.doctorCases"
        :has-appointment-section="!!appointmentSection"
        x-space-class="px-4"
        @click-appointment="scrollToAppointment"
        @click-services-details="handleClickServicesDetails"
      />
      <div
        v-if="casesData.hasNextPage"
        class="d-flex py-8 ma-auto"
      >
        <v-progress-circular
          v-intersect="handleIntersection"
          :data-qa="isMobile ? 'doctor-cases_dialog_progress' : 'doctor-cases_drawer_progress'"
          size="40"
          width="2.8"
          color="primary"
          class="ma-auto"
          indeterminate
        />
      </div>
    </CoreDrawer>
    <ServiceDetailsModal
      v-model="caseServicesData.isVisibleModal"
      :total-price="totalPrice"
      :is-doctor-work-at-lpu="caseServicesData.caseServicesData.is_doctor_work_at_lpu"
    >
      <v-expand-transition>
        <div
          v-if="caseServicesData.isLoading"
          class="progress-circular"
        >
          <v-progress-circular
            data-qa="doctor-cases_service_details_modal_progress"
            size="32"
            width="2"
            color="primary"
            class="ma-auto"
            indeterminate
          />
        </div>
        <ServiceDetailsContent
          v-else
          :services-data="caseServicesData.caseServicesData"
        />
      </v-expand-transition>
    </ServiceDetailsModal>
    <CoreDialog
      v-model="alertDialogActive"
      data-qa="doctor-cases-alert-modal"
      size="sm"
      :title="alertDialogProps.title"
    >
      <div class="text-body-1 text-secondary--text">
        {{ alertDialogProps.contentText }}
      </div>
      <template #actions>
        <v-btn
          data-qa="modal-actions-btn-close"
          color="primary"
          text
          @click="alertDialogActive = false"
        >
          {{ alertDialogProps.confirmText }}
        </v-btn>
      </template>
    </CoreDialog>
  </div>
</template>

<script lang="ts" setup>
import i18next from 'i18next'
import { onMounted, ref } from 'vue'
import getUserLanguage from 'src/utils/getUserLanguage'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import CoreDrawer from 'src/core/CoreDrawer/CoreDrawer.vue'
import type { Doctor, DoctorCase } from 'www/vue_components/common/DoctorCases/types'
import { DIALOG_PROPS } from 'www/vue_components/common/DoctorCases/constants'
import useCases from 'www/vue_components/common/DoctorCases/composables/useCases'
import CasesDetails from 'www/vue_components/common/DoctorCases/components/CasesDetails.vue'
import PreviewCasesSlider from 'www/vue_components/common/DoctorCases/components/PreviewCasesSlider.vue'
import useCaseServices from 'www/vue_components/common/DoctorCases/composables/useCaseServices'
import ServiceDetailsModal from 'www/vue_components/common/DoctorCases/components/ServiceDetailsModal.vue'
import ServiceDetailsContent from 'www/vue_components/common/DoctorCases/components/ServiceDetailsContent.vue'

const props = defineProps<{
  doctor: Doctor
  doctorCaseCount: number
  doctorCases: DoctorCase[]
}>()

const isMobile = window.MOBILE_VERSION
const userLanguage = getUserLanguage()

const appointmentSection = ref<Element | null>(null)

const alertDialogProps = ref<typeof DIALOG_PROPS[keyof typeof DIALOG_PROPS]>(DIALOG_PROPS.default)
const alertDialogActive = ref(false)

alertDialogProps.value = {
  title: i18next.t(alertDialogProps.value.title, { ns: userLanguage }),
  contentText: i18next.t(alertDialogProps.value.contentText, { ns: userLanguage }),
  // @ts-ignore
  confirmText: i18next.t(alertDialogProps.value.confirmText, { ns: userLanguage }),
}

const casesDetailsRef = ref()
const casesData = useCases({
  doctorId: props.doctor.id,
  alertDialogProps,
  alertDialogActive,
})

const caseServicesData = useCaseServices({
  alertDialogProps,
  alertDialogActive,
})
const totalPrice = ref(0)

function handleClickServicesDetails(caseDetails: typeof casesData['doctorCases'][number]) {
  caseServicesData.openModal(caseDetails.id)
  totalPrice.value = caseDetails.total_price
}

async function handleClickSlide(doctorCase?: DoctorCase) {
  await casesData.openModal()

  if (doctorCase) {
    setTimeout(() => {
      if (casesDetailsRef.value) {
        casesDetailsRef.value.scrollToCase(doctorCase.id)
      }
    })
  }
}

function scrollToAppointment() {
  if (appointmentSection.value) {
    casesData.closeModal()
    setTimeout(() => {
      appointmentSection.value!.scrollIntoView()
      window.dispatchEvent(new Event('scroll'))
      window.dispatchEvent(new Event('redraw'))
    })
  }
}

function handleIntersection([event]: IntersectionObserverEntry[]) {
  if (event?.isIntersecting) {
    casesData.loadNextCases()
  }
}

onMounted(() => {
  appointmentSection.value = document.getElementById('doctor-page-lpu-list')

  if (!appointmentSection.value) {
    window.addEventListener('doctor-page-lpu-list-mounted', () => {
      appointmentSection.value = document.getElementById('doctor-page-lpu-list')
    }, { once: true })
  }
})
</script>

<style scoped>
.progress-circular {
  display: flex;
  width: 100%;
}
</style>
